import { DateTime } from 'luxon';
import { AuthRole, authRoleFromStrings, authRoleToStrings } from '../couch-auth';
import {
  CouchUserInfo,
  NEVER_EXPIRES,
  SubscriptionStatus,
  UserLicenseType,
  kPPGLicenseGracePeriodHours,
} from './couch-user';

export { NEVER_EXPIRES };

/**
 * The three fields on a grant block that this file reads and seeds. Every
 * per-provider block type on `CouchUserInfo` carries them; a block's other
 * fields belong to whichever writer owns that block and are none of this
 * file's business.
 */
export interface LicenseBlock {
  subscriptionStatus?: SubscriptionStatus;
  expiresDate_utc?: string;
  grantedRole?: AuthRole;
}

/**
 * Everything this file and `user-conflict-merge.ts` need to know about ONE
 * payment rail — one way somebody can be holding a license.
 *
 * WHY THIS TYPE EXISTS. `LICENSE_RAILS` below holds exactly one row per member
 * of `UserLicenseType`, and it is the only list of them anywhere. Before it,
 * the same rails were written out by hand four separate times: the tie-break
 * order, the lazy-migration pass, the candidate-building pass, and the
 * freshest-block merge in `user-conflict-merge.ts`. Every way of forgetting one
 * failed silently, and each failed differently — a rail missing from the
 * tie-break order got `-1` back from `indexOf`, so it won every tie instead of
 * losing it; one missing from the candidate pass never became a candidate at
 * all, so a paying customer's block granted them nothing; one missing from the
 * migration pass kept whatever partial legacy shape it arrived with; and one
 * missing from the merge lost its grant whenever two of the user's devices
 * wrote the record at the same moment. Nothing reported any of the four. A rail
 * that is not in the table now fails to compile instead.
 *
 * Each row names its own block field in more than one accessor below. That
 * repetition is deliberate, and it is the cheap kind: three adjacent lines the
 * compiler checks against the real field types, rather than four lists in two
 * files that nothing checks at all.
 */
export interface LicenseRail {
  /** The license type this rail grants. */
  provider: UserLicenseType;

  /** This rail's grant block on a user record, or undefined when it has none. */
  readBlock(info: CouchUserInfo): LicenseBlock | undefined;

  /**
   * This rail's "last active" timestamp, as stored. Which field that is differs
   * per rail — `lastValidated_utc` on the webhook-backed providers,
   * `licenseActivated_utc` on PayPro, `grantedAt_utc` on Coach,
   * `redeemedAt_utc` on Key — which is exactly why it is written down once here
   * rather than restated by each reader.
   *
   * It ranks lapsed blocks against each other when picking a winner, and picks
   * which conflicting branch to keep in `mergeUserRevisions`.
   */
  readFreshness(info: CouchUserInfo): string | undefined;

  /**
   * Copies this rail's block from `source` onto `target`, and clears it when
   * `source` is undefined — which is what "no revision carried this block"
   * means to `mergeUserRevisions`. It always assigns, so the merged record ends
   * up with exactly the same set of keys it had when each rail was written out
   * by hand.
   */
  copyBlock(target: CouchUserInfo, source: CouchUserInfo | undefined): void;

  /**
   * True when seeding a block's missing `expiresDate_utc` from the top-level
   * value must be SKIPPED because that top-level value is `NEVER_EXPIRES`.
   *
   * A `'never'` expiry on any of the three coach rails is uniquely dangerous:
   * `coachData` is a client's grant, and #4361 Phase 1 makes it a verbatim copy
   * of the coach's block, so a `'never'` that lands on `coachTierData` or
   * `coachSeatData` is fanned out to every client on the roster and grants them
   * all Premium forever — the exact leak Phase 1 exists to close.
   *
   * The guard is deliberately NARROW: it blocks only the `'never'` source, not
   * seeding in general. A blanket coach-rail skip would break a genuinely
   * PAYING coach. `applyPaddleCoachSeatEvent`'s `activateThrough` sets the
   * status to Active FIRST and then calls `updateExpiry`, which early-returns
   * when the event carried no resolvable period end (the #3539 "never overwrite
   * a good expiry with a missing value" rule). That leaves a granting status
   * with an undefined expiry — a real, reachable shape. Skipping the seed there
   * would resolve the block to -Infinity and strip the coach's own Premium, and
   * through the mirror every one of their clients' too. A real top-level date
   * must still seed normally.
   *
   * This is NOT the same question as {@link blockOwnsItsExpiry}, and the two
   * must never be folded into one column: this one is true for the 3 coach
   * rails, that one for 5 rails. Merging them would stop an indefinite expiry
   * seeding onto the Apple and Paddle blocks, which resolves those customers to
   * -Infinity and strips paid access.
   */
  neverSeedIndefiniteExpiry: boolean;

  /**
   * True when this rail's block is the CANONICAL copy of its own expiry, so the
   * legacy grace correction must never copy a later top-level value over it.
   *
   * That correction exists only for legacy PayPro / Google Play data, where the
   * `kPPGLicenseGracePeriodHours` grace once lived on top-level
   * `licenseExpires_utc` while the block held the raw expiry. Where it still
   * applies, a top-level value LATER than the block's is the graced version and
   * is preferred, so up to 24h of grace is not lost before the next webhook
   * overwrites the block. See "License grace period — provider asymmetry" in
   * design-docs/iap-subscriptions.md.
   *
   * For every rail marked here the correction is not merely unnecessary but
   * actively wrong, because their expiry legitimately moves EARLIER and this
   * correction would silently undo that. Each row below says why for its own
   * rail. The load-bearing reason it is safe to skip in all of those cases is
   * the same: top-level is DERIVED from the winning block at the end of this
   * same reconciler pass, so the two agree in every healthy state and the
   * correction could only ever fire on a value a writer just deliberately
   * lowered.
   */
  blockOwnsItsExpiry: boolean;

  /**
   * How many hours of OUR OWN access grace this rail's writers add to the real
   * provider period end before storing it in `expiresDate_utc`.
   *
   * `0` means the stored value IS the provider's own date. A positive number
   * means the stored value is that date plus this many hours, so subtracting
   * them recovers the moment the provider actually charges, pauses or cancels.
   * `undefined` means this rail cannot know — the Coach mirror copies a coach's
   * expiry without recording which store the coach pays through.
   *
   * NOT the same thing as {@link isInGrace}, despite the shared word. That asks
   * "is this block still granting access?" and has nothing to do with the 24h
   * access grace this column describes.
   *
   * It is a function of the record, not a constant, because `CoachTier` spans
   * two stores with different answers: Apple stores a raw expiry, Google Play
   * adds the grace. See "License grace period — provider asymmetry" in
   * design-docs/iap-subscriptions.md, and
   * `shared/src/data-model/subscription-end-instants.ts` for the one place that
   * turns this into a date a person is shown.
   */
  readStoredExpiryGraceHours(info: Pick<CouchUserInfo, 'coachTierData'>): number | undefined;
}

/**
 * Every payment rail, in TIE-BREAK ORDER — the deterministic order used when
 * two candidates are otherwise equal. PayPro is the historical primary
 * provider, so it wins ties.
 *
 * The ORDER of these rows is meaningful and the ONLY thing that reads it is the
 * tie-break; the migration pass and the candidate-building pass below both
 * loop over the same rows and are order-free (each touches only its own rail's
 * block, and the winner-selection reduces compare with strict `>` / `<` and
 * fall through to a total-ordered tie-break). So reordering rows changes
 * tie-breaks and nothing else.
 */
export const LICENSE_RAILS = [
  {
    provider: UserLicenseType.PayPro,
    readBlock: info => info.payProData,
    readFreshness: info => info.payProData?.licenseActivated_utc,
    copyBlock: (target, source) => { target.payProData = source?.payProData; },
    neverSeedIndefiniteExpiry: false,
    // legacy PayPro data is exactly what the grace correction was written for.
    blockOwnsItsExpiry: false,
    // PayPro IPNs lag the real charge, so its writers store nextPayment + 24h.
    readStoredExpiryGraceHours: () => kPPGLicenseGracePeriodHours,
  },
  {
    // Paddle is the other web provider. It sits just after PayPro: on a
    // same-expiry tie between the two web rails, PayPro (the historical
    // primary) still wins, but Paddle outranks the store providers.
    provider: UserLicenseType.Paddle,
    readBlock: info => info.paddleData,
    readFreshness: info => info.paddleData?.lastValidated_utc,
    copyBlock: (target, source) => { target.paddleData = source?.paddleData; },
    neverSeedIndefiniteExpiry: false,
    // Paddle arrived long after the grace refactor and never had the legacy
    // shape, so there is nothing here for the correction to repair — and it
    // bites whenever an expiry legitimately moves EARLIER (#4248 Phase 3). The
    // Paddle writers that apply the grace do it at WRITE time, and the two that
    // deliberately do not — the refund/chargeback `endNow` and the re-sync's
    // purge clamp — write an intentionally ungraced date the correction must not
    // undo either.
    //
    // Meanwhile the correction actively breaks the admin re-sync. When support
    // moves a renewal date earlier in the Paddle dashboard, the reconciler writes
    // the new, EARLIER expiry onto the block — precisely the moment top-level
    // (still the old, later value) exceeds it. The correction would copy the
    // stale later date straight back over the correction just made, so the
    // customer keeps entitlement past the date Paddle says they paid for, and
    // the operator is told the account was updated.
    blockOwnsItsExpiry: true,
    // Every Paddle write goes through updateExpiry(..., kPPGLicenseGracePeriodHours).
    readStoredExpiryGraceHours: () => kPPGLicenseGracePeriodHours,
  },
  {
    provider: UserLicenseType.Apple,
    readBlock: info => info.appleData,
    readFreshness: info => info.appleData?.lastValidated_utc,
    copyBlock: (target, source) => { target.appleData = source?.appleData; },
    neverSeedIndefiniteExpiry: false,
    // The Apple block intentionally stores the RAW Apple expiry with no
    // PPG-style 24h grace (Apple has its own grace_period / billing_retry
    // statuses). If top-level still carries a stale graced value from before the
    // Apple-grace-removal change deployed, the correction would copy that grace
    // BACK into the block on every load — undoing the no-grace fix.
    blockOwnsItsExpiry: true,
    // Apple has its own grace_period / billing_retry statuses, so we store the
    // RAW Apple expiry and the stored value already IS Apple's own date.
    readStoredExpiryGraceHours: () => 0,
  },
  {
    provider: UserLicenseType.GooglePlay,
    readBlock: info => info.googlePlayData,
    readFreshness: info => info.googlePlayData?.lastValidated_utc,
    copyBlock: (target, source) => { target.googlePlayData = source?.googlePlayData; },
    neverSeedIndefiniteExpiry: false,
    // legacy Google Play data carried the grace on top-level too, so the
    // correction still applies here exactly as it does to PayPro.
    blockOwnsItsExpiry: false,
    // RTDNs lag the real charge the same way PayPro's IPNs do.
    readStoredExpiryGraceHours: () => kPPGLicenseGracePeriodHours,
  },
  {
    // CoachTier sits adjacent to the store providers: it is a store
    // (Apple / Google Play) subscription too, just a coach-tier one. Its
    // position only matters for same-expiry tie-breaks.
    provider: UserLicenseType.CoachTier,
    readBlock: info => info.coachTierData,
    readFreshness: info => info.coachTierData?.lastValidated_utc,
    copyBlock: (target, source) => { target.coachTierData = source?.coachTierData; },
    neverSeedIndefiniteExpiry: true,
    // A coach block the client mirror is read FROM, so it has to be a
    // trustworthy input: a coach who churns and resubscribes at a different
    // date, or whose refund calls `endNow`, moves earlier for legitimate
    // reasons. It also anchors recency on its own `lastValidated_utc` rather
    // than on top-level, so it needs no cross-field repair.
    blockOwnsItsExpiry: true,
    // The one rail whose answer is not a constant: this block spans BOTH stores
    // and they disagree. Apple stores the raw expiry; the Google Play writers add
    // the grace, exactly as they do for personal googlePlayData. With no block
    // there is no store to ask, so the answer is unknown rather than guessed.
    readStoredExpiryGraceHours: info => {
      const provider = info.coachTierData?.provider;
      if (provider === undefined) return undefined;
      return provider === 'apple' ? 0 : kPPGLicenseGracePeriodHours;
    },
  },
  {
    // CoachSeat is the web (Paddle) coach rail — the sibling of CoachTier.
    // It sits just after Paddle-the-personal-web-rail in spirit, but for
    // same-expiry tie-breaks it is placed next to CoachTier (both are
    // coaching-granted Premium). Position only matters on an exact-expiry tie.
    provider: UserLicenseType.CoachSeat,
    readBlock: info => info.coachSeatData,
    readFreshness: info => info.coachSeatData?.lastValidated_utc,
    copyBlock: (target, source) => { target.coachSeatData = source?.coachSeatData; },
    neverSeedIndefiniteExpiry: true,
    // The other coach block the client mirror reads FROM — same reasoning as
    // CoachTier. This rail's gap was closed by #4361 Phase 1.
    blockOwnsItsExpiry: true,
    // The web coach rail: same writers, same grace, as personal paddleData.
    readStoredExpiryGraceHours: () => kPPGLicenseGracePeriodHours,
  },
  {
    provider: UserLicenseType.Key,
    readBlock: info => info.keyData,
    readFreshness: info => info.keyData?.redeemedAt_utc,
    copyBlock: (target, source) => { target.keyData = source?.keyData; },
    neverSeedIndefiniteExpiry: false,
    blockOwnsItsExpiry: false,
    // Nothing ever adds grace to a redeemed key's expiry.
    readStoredExpiryGraceHours: () => 0,
  },
  {
    provider: UserLicenseType.Coach,
    readBlock: info => info.coachData,
    readFreshness: info => info.coachData?.grantedAt_utc,
    copyBlock: (target, source) => { target.coachData = source?.coachData; },
    neverSeedIndefiniteExpiry: true,
    // `coachData` is a client's grant, and #4361 Phase 1 makes it a verbatim
    // copy of whichever block backs their coach. Revoking a lapsed coach's
    // clients IS a deliberate move to an earlier date, so leaving the correction
    // active would restore the older, longer date and silently undo every
    // revocation — the exact leak Phase 1 exists to close. It also anchors
    // recency on its own `grantedAt_utc` rather than on top-level.
    blockOwnsItsExpiry: true,
    // Unknowable by construction. `coachData` is a verbatim copy of whichever
    // block backs the coach, and the copy does not carry which store that was —
    // so we cannot tell whether the date it holds is graced. Answering `undefined`
    // makes the provider-acts question return no date instead of a date that is
    // silently a day wrong. See coach-license-mirror.ts.
    readStoredExpiryGraceHours: () => undefined,
  },
] as const satisfies readonly LicenseRail[];

/**
 * The compile-time proof that the table above is COMPLETE. `MissingRail` is
 * `never` while every member of `UserLicenseType` has a row; the moment one
 * does not, it becomes that member and this line stops compiling.
 *
 * This is the whole point of the table, so do not weaken it: it is what turns
 * "somebody forgot one of the lists" from a silent wrong answer about who has
 * paid into a build failure.
 */
type MissingRail = Exclude<UserLicenseType, (typeof LICENSE_RAILS)[number]['provider']>;
const _everyProviderHasARail: never[] = [] as MissingRail[];
void _everyProviderHasARail;

interface BlockCandidate {
  provider: UserLicenseType;
  /** This rail's position in {@link LICENSE_RAILS}, which IS the tie-break order. */
  tieBreakRank: number;
  status: SubscriptionStatus;
  expires: string | undefined;
  grantedRole: AuthRole;
  lastActive: number; // epoch ms; -Infinity if unknown
}

/**
 * An expiry as comparable epoch millis, with the two sentinel shapes this
 * codebase relies on: `'never'` is `+Infinity` (grants forever) and a missing
 * or unparseable value is `-Infinity` (grants nothing).
 *
 * Exported because "does this change reduce access?" has to be asked outside
 * the reconciler too — the #4361 login refresh compares a client's stored
 * `coachData` expiry against a freshly resolved one, and every client in the
 * existing population still holds the literal string `'never'`. A naive
 * `DateTime.fromISO` comparison is invalid for all of them, so the repair would
 * silently never fire on exactly the population it exists to fix. Use this
 * rather than re-deriving the rule.
 */
export function expiresMillis(expires: string | undefined): number {
  if (expires === NEVER_EXPIRES) return Number.POSITIVE_INFINITY;
  if (expires === undefined) return Number.NEGATIVE_INFINITY;
  const dt = DateTime.fromISO(expires);
  return dt.isValid ? dt.toMillis() : Number.NEGATIVE_INFINITY;
}

/**
 * True when a per-provider block currently grants access: an
 * access-granting status (Active / Suspended / Temporary) with a future
 * — or `'never'` — expiry. Exported so other entitlement code (e.g. the
 * coach-tier capability resolver) shares this single definition rather
 * than reimplementing it and risking drift.
 */
export function isInGrace(status: SubscriptionStatus, expires: string | undefined, nowMillis: number): boolean {
  if (!isAccessGrantingStatus(status)) return false;
  const ms = expiresMillis(expires);
  if (ms === Number.POSITIVE_INFINITY) return true;
  return ms > nowMillis;
}

/**
 * The status half of {@link isInGrace}, on its own: true when a status grants
 * access at all, before the expiry is even looked at.
 *
 * Temporary is PayPro's 24h provisional access during manual review; it grants
 * access symmetrically with Active/Suspended. Rationale + audit-log breakdown in
 * design-docs/subscription-state-reconciliation.md.
 *
 * This is an ALLOW-LIST on purpose: any status NOT named here is non-granting by
 * omission. That is what makes `SubscriptionStatus.Paused` (the Paddle
 * self-service pause, #4189) correctly drop access the moment the pause takes
 * effect — do NOT add Paused here, and do not rewrite this as a deny-list, or a
 * paused subscription would keep its entitlement.
 *
 * Exported so callers that need "would this status grant?" without a clock —
 * the #4361 login refresh asks exactly that when deciding whether a change
 * reduces access — share this one definition instead of restating the list and
 * letting it drift.
 */
export function isAccessGrantingStatus(status: SubscriptionStatus | undefined): boolean {
  return status === SubscriptionStatus.Active ||
    status === SubscriptionStatus.Suspended ||
    status === SubscriptionStatus.Temporary;
}

function isoToMillis(iso: string | undefined): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toMillis() : Number.NEGATIVE_INFINITY;
}

/**
 * True when seeding a block's missing `expiresDate_utc` from the top-level
 * value would turn this rail into a PERMANENT grant. The rails this can happen
 * to, and the full reasoning, are on {@link LicenseRail.neverSeedIndefiniteExpiry}.
 */
function seedWouldGrantForever(rail: LicenseRail, info: CouchUserInfo): boolean {
  if (info.licenseExpires_utc !== NEVER_EXPIRES) return false;
  return rail.neverSeedIndefiniteExpiry;
}

/**
 * Per-field lazy migration: each of {subscriptionStatus, expiresDate_utc,
 * grantedRole} on a block is seeded independently if missing. This
 * matters because legacy `payProData` already has `subscriptionStatus`
 * set — a status-only sentinel would skip seeding `expiresDate_utc` and
 * `grantedRole` for the entire installed base.
 *
 * - Matching-provider (`info.licenseType === provider`): seed from
 *   top-level fields (`subscriptionStatus`, `licenseExpires_utc`,
 *   `authRoleFromStrings(info.roles)`).
 * - Orphan provider: status → `Terminated`, expires → leave undefined,
 *   grantedRole → `Paid`.
 */
function migrateBlock(rail: LicenseRail, info: CouchUserInfo): void {
  const block = rail.readBlock(info);
  if (!block) return;
  const isMatching = info.licenseType === rail.provider;
  if (block.subscriptionStatus === undefined) {
    block.subscriptionStatus = isMatching ? (info.subscriptionStatus ?? SubscriptionStatus.None) : SubscriptionStatus.Terminated;
  }
  if (isMatching) {
    if (block.expiresDate_utc === undefined && !seedWouldGrantForever(rail, info)) {
      block.expiresDate_utc = info.licenseExpires_utc;
    } else if (
      // Rails whose block is the canonical copy of its own expiry skip the
      // correction entirely — running it there would undo a deliberate move to
      // an EARLIER date. Which rails those are, and why for each of them, is on
      // `LicenseRail.blockOwnsItsExpiry` and in the rows of `LICENSE_RAILS`.
      !rail.blockOwnsItsExpiry &&
      info.licenseExpires_utc &&
      info.licenseExpires_utc !== NEVER_EXPIRES
    ) {
      // Legacy-data correction (PPG / GooglePlay): pre-refactor, the
      // `kPPGLicenseGracePeriodHours` grace was applied only to top-level
      // `licenseExpires_utc`. The block had the raw expiry. If the top-level
      // value is LATER than the block value, it's the graced version —
      // prefer it to avoid losing up to 24h of grace until the next webhook
      // overwrites the block. See "License grace period — provider asymmetry"
      // in design-docs/iap-subscriptions.md.
      const blockMs = expiresMillis(block.expiresDate_utc);
      const topMs = expiresMillis(info.licenseExpires_utc);
      if (Number.isFinite(blockMs) && Number.isFinite(topMs) && topMs > blockMs) {
        block.expiresDate_utc = info.licenseExpires_utc;
      }
    }
  }
  if (block.grantedRole === undefined) {
    block.grantedRole = isMatching ? authRoleFromStrings(info.roles) : AuthRole.Paid;
  }
}

function tieBreak(a: BlockCandidate, b: BlockCandidate): number {
  return a.tieBreakRank - b.tieBreakRank;
}

/**
 * Reconcile per-provider license blocks to top-level derived fields.
 * MUTATES `info` in place. Pure (no I/O).
 *
 * See `design-docs/subscription-state-reconciliation.md` for the full
 * algorithm.
 */
export function reconcileLicenseState(info: CouchUserInfo, now?: DateTime): void {
  const nowMillis = (now ?? DateTime.utc()).toMillis();

  // Phase 1: per-field lazy migration on each present block. Order-free: each
  // rail touches only its own block and writes none of the top-level fields the
  // others read.
  for (const rail of LICENSE_RAILS) {
    migrateBlock(rail, info);
  }

  // Phase 2: build candidates from every present block, reading each rail's own
  // "last active" timestamp field through its row. An active coach-tier or
  // coach-seat subscription grants the coach personal Premium (default
  // grantedRole = Paid) and is gated by isInGrace exactly like the store
  // blocks; grace handling (Apple's raw expiry, Google Play's and the web
  // rails' +kPPGLicenseGracePeriodHours) is applied by the writers, not here.
  const candidates: BlockCandidate[] = [];
  // for each rail that has a block, build a candidate; the row's index is its
  // tie-break rank
  for (const [tieBreakRank, rail] of LICENSE_RAILS.entries()) {
    const block = rail.readBlock(info);
    if (!block) continue;
    // guard a damaged or old-shaped document: a stamp that is not a string
    // ranks as "unknown", the same as one that is missing. Behaviour-neutral —
    // isoToMillis already answered -Infinity for such a value, because Luxon
    // reports a non-string as an INVALID DateTime rather than throwing.
    const lastActive = rail.readFreshness(info);
    candidates.push({
      provider: rail.provider,
      tieBreakRank,
      status: block.subscriptionStatus ?? SubscriptionStatus.None,
      expires: block.expiresDate_utc,
      grantedRole: block.grantedRole ?? AuthRole.Paid,
      lastActive: isoToMillis(typeof lastActive === 'string' ? lastActive : undefined),
    });
  }

  // Preserve admin-band roles - Mod, Dev, Officer, ServerDev - across
  // reconciliation. Those are assigned by another system entirely and must not be
  // taken away by a change of subscription state.
  //
  // Vip and Paid are NOT preserved here, and an earlier version of this comment
  // wrongly said Vip was. They sit below the threshold on purpose: they are the
  // two roles a subscription itself can grant, so preserving them would make any
  // account that ever held one keep it forever, whatever its payment records say.
  // An officer granting either of those by hand therefore has to leave a record
  // the reconciler can find - a `keyData` block, written by the admin permissions
  // screen. See `shared/src/data-model/officer-grant.ts`.
  const existingRole = authRoleFromStrings(info.roles);
  const adminBandThreshold = AuthRole.Mod;
  const preserveExistingRole = existingRole >= adminBandThreshold;

  if (candidates.length === 0) {
    info.licenseType = undefined;
    info.subscriptionStatus = undefined;
    info.licenseExpires_utc = undefined;
    info.roles = preserveExistingRole ? authRoleToStrings(existingRole) : authRoleToStrings(AuthRole.Unknown);
    return;
  }

  // Phase 3: partition.
  const hasAccess = candidates.filter(c => isInGrace(c.status, c.expires, nowMillis));
  const noAccess = candidates.filter(c => !isInGrace(c.status, c.expires, nowMillis));

  // Phase 4: winner selection.
  let winner: BlockCandidate;
  let winnerInHasAccess: boolean;
  if (hasAccess.length > 0) {
    winnerInHasAccess = true;
    winner = hasAccess.reduce((best, c) => {
      const cm = expiresMillis(c.expires);
      const bm = expiresMillis(best.expires);
      if (cm > bm) return c;
      if (cm < bm) return best;
      return tieBreak(c, best) < 0 ? c : best;
    });
  } else {
    winnerInHasAccess = false;
    // A PAUSED block outranks every other no-access block, ahead of recency
    // (#4189). A paused subscription is dormant but LIVE — the user can resume
    // it at any time — whereas everything else in this bucket is lapsed or dead.
    //
    // Recency alone gets this wrong, and durably so: `subscription.paused` does
    // not refresh `lastValidated_utc`, while `couchLogin` re-validates a
    // residual Apple / Google Play block whenever it is over a day stale (it
    // skips only a Terminated user, and a paused user is `Paused`). So within a
    // day of pausing, a DEAD store block becomes the freshest candidate and
    // wins. `licenseType` then flips away from Paddle, which switches off every
    // paused affordance at once — the paused note, the Resume button, the
    // manage callable — and, worst, the Subscribe page stops recognising the
    // membership and offers a fresh purchase while the paused subscription is
    // still resumable at Paddle. That is the double bill this whole feature
    // exists to prevent. Ranking by liveness rather than by stamp freshness
    // fixes it for good instead of racing the re-validation.
    //
    // Applies to ANY provider's block, not just the personal Paddle one. Only
    // Paddle produces `Paused` today, so in practice this ranks exactly the case
    // described above — but the rule is written on the status rather than on the
    // provider deliberately: a dormant-but-resumable block is the better winner
    // whoever issued it, and scoping it to one provider would silently stop
    // working the day another rail gains a pause.
    //
    // named for what it actually tests — the block's status. Being Paused is what
    // makes the block dormant-but-live; there is no separate resumability term to
    // check, and a name that implied one would invite a future reader to add it.
    const isPaused = (c: BlockCandidate) => c.status === SubscriptionStatus.Paused;
    winner = noAccess.reduce((best, c) => {
      const candidatePaused = isPaused(c);
      const bestPaused = isPaused(best);
      if (candidatePaused !== bestPaused) return candidatePaused ? c : best;
      if (c.lastActive > best.lastActive) return c;
      if (c.lastActive < best.lastActive) return best;
      return tieBreak(c, best) < 0 ? c : best;
    });
  }

  // Phase 5: apply. Take max(existing admin role, computed subscription role)
  // so an out-of-band admin assignment cannot be downgraded by a subscription
  // transition (and a Paid/Vip subscription can still upgrade an Unknown user).
  info.licenseType = winner.provider;
  info.subscriptionStatus = winner.status;
  info.licenseExpires_utc = winner.expires;
  const subscriptionRole = winnerInHasAccess ? winner.grantedRole : AuthRole.Unknown;
  const finalRole = preserveExistingRole ? Math.max(existingRole, subscriptionRole) : subscriptionRole;
  info.roles = authRoleToStrings(finalRole);
}
